import { normalizeTitle, validTitle } from './title-model';

/** Small deterministic teaching models. No file system or Python process is connected. */
export interface StoredTask {
  id: number;
  title: string;
  completed: boolean;
}
export interface StoredState {
  next_id: number;
  tasks: StoredTask[];
}
export function emptyStoredState(): StoredState {
  return { next_id: 1, tasks: [] };
}
export function exampleStoredState(): StoredState {
  return {
    next_id: 4,
    tasks: [
      { id: 1, title: 'Read notes', completed: false },
      { id: 2, title: 'Café review', completed: true },
      { id: 3, title: 'Call Alex', completed: false },
    ],
  };
}
export function copyStoredState(state: StoredState): StoredState {
  return { next_id: state.next_id, tasks: state.tasks.map((task) => ({ ...task })) };
}
export const storedText = (state: StoredState) => JSON.stringify(state, null, 2);

export function identityFrame(step: number, removeAll: boolean) {
  const initial = exampleStoredState();
  const remaining = step >= 1 ? (removeAll ? [] : initial.tasks.slice(0, -1)) : initial.tasks;
  const full = { next_id: 4, tasks: remaining.map((task) => ({ ...task })) };
  const recordsOnly = copyStoredState(full);
  if (step >= 3) recordsOnly.next_id = Math.max(0, ...remaining.map((task) => task.id)) + 1;
  if (step >= 4) {
    for (const state of [recordsOnly, full]) {
      state.tasks.push({ id: state.next_id, title: 'New task', completed: false });
      state.next_id++;
    }
  }
  return {
    recordsOnly,
    full,
    recordsFile: step >= 2 ? JSON.stringify(remaining, null, 2) : null,
    fullFile: step >= 2 ? storedText({ next_id: 4, tasks: remaining }) : null,
  };
}

export type LoadExample =
  | 'valid'
  | 'missing'
  | 'unreadable'
  | 'utf8'
  | 'syntax'
  | 'shape'
  | 'duplicate'
  | 'boolean'
  | 'title'
  | 'counter'
  | 'boolean-id';
export const loadExampleOptions = [
  { value: 'valid', label: 'Valid snapshot' },
  { value: 'missing', label: 'No saved file yet' },
  { value: 'unreadable', label: 'File cannot be opened' },
  { value: 'utf8', label: 'Bytes are not valid UTF-8' },
  { value: 'syntax', label: 'Malformed JSON text' },
  { value: 'shape', label: 'JSON has the wrong shape' },
  { value: 'duplicate', label: 'Two records share an ID' },
  { value: 'boolean', label: 'Completion is a string' },
  { value: 'title', label: 'Title contains only spaces' },
  { value: 'counter', label: 'Counter reuses an existing ID' },
  { value: 'boolean-id', label: 'ID is a boolean' },
] as const;
interface LoadStep {
  title: string;
  detail: string;
  outcome: 'pass' | 'stop' | 'empty';
}
export function inspectStoredState(value: unknown): LoadStep[] {
  const steps: LoadStep[] = [];
  if (
    typeof value !== 'object' ||
    value === null ||
    Array.isArray(value) ||
    !('tasks' in value) ||
    !Array.isArray(value.tasks) ||
    !('next_id' in value)
  )
    return [
      {
        title: 'Check the shape',
        detail: 'Expected a dictionary with tasks and next_id.',
        outcome: 'stop',
      },
    ];
  steps.push({
    title: 'Check the shape',
    detail: 'A dictionary contains a tasks list and next_id.',
    outcome: 'pass',
  });
  const ids = new Set<number>();
  for (const task of value.tasks as unknown[]) {
    if (
      typeof task !== 'object' ||
      task === null ||
      !('id' in task) ||
      typeof task.id !== 'number' ||
      !Number.isSafeInteger(task.id) ||
      task.id < 1
    ) {
      steps.push({
        title: 'Check every record',
        detail: 'Each ID must be a positive whole number, not a boolean.',
        outcome: 'stop',
      });
      return steps;
    }
    if (ids.has(task.id)) {
      steps.push({
        title: 'Check every record',
        detail: `ID ${task.id} appears twice. Identity must be unique.`,
        outcome: 'stop',
      });
      return steps;
    }
    ids.add(task.id);
    if (
      !('title' in task) ||
      typeof task.title !== 'string' ||
      task.title !== normalizeTitle(task.title) ||
      !validTitle(task.title)
    ) {
      steps.push({
        title: 'Check every record',
        detail: 'Every title must be trimmed text with 1–80 characters.',
        outcome: 'stop',
      });
      return steps;
    }
    if (!('completed' in task) || typeof task.completed !== 'boolean') {
      steps.push({
        title: 'Check every record',
        detail: 'completed must be a boolean. The text "false" is a string.',
        outcome: 'stop',
      });
      return steps;
    }
  }
  steps.push({
    title: 'Check every record',
    detail: 'IDs are unique; titles and completion values satisfy the application rules.',
    outcome: 'pass',
  });
  if (
    typeof value.next_id !== 'number' ||
    !Number.isSafeInteger(value.next_id) ||
    value.next_id <= Math.max(0, ...ids)
  ) {
    steps.push({
      title: 'Check the counter',
      detail: 'next_id must be a positive whole number larger than every saved ID.',
      outcome: 'stop',
    });
    return steps;
  }
  steps.push({
    title: 'Check the counter',
    detail: 'next_id is larger than every saved ID.',
    outcome: 'pass',
  });
  steps.push({
    title: 'Accept the state',
    detail: 'Only now may these values become the running application state.',
    outcome: 'pass',
  });
  return steps;
}
export function loadingTrace(example: LoadExample) {
  const state = exampleStoredState();
  let text: string;
  switch (example) {
    case 'shape':
      text = '[]';
      break;
    case 'syntax':
      text = '{"tasks": [';
      break;
    case 'duplicate':
      text = JSON.stringify({ ...state, tasks: [state.tasks[0], state.tasks[0]] }, null, 2);
      break;
    case 'boolean':
      text = JSON.stringify(
        { next_id: 2, tasks: [{ id: 1, title: 'Read notes', completed: 'false' }] },
        null,
        2,
      );
      break;
    case 'title':
      text = JSON.stringify(
        { next_id: 2, tasks: [{ id: 1, title: '   ', completed: false }] },
        null,
        2,
      );
      break;
    case 'counter':
      text = storedText({ ...state, next_id: 3 });
      break;
    case 'boolean-id':
      text = JSON.stringify(
        { next_id: 2, tasks: [{ id: true, title: 'Read notes', completed: false }] },
        null,
        2,
      );
      break;
    default:
      text = storedText(state);
  }
  if (example === 'missing')
    return {
      source: 'No file exists.',
      steps: [
        {
          title: 'Open the file',
          detail: 'First run: create empty state in memory. No stored data is replaced.',
          outcome: 'empty' as const,
        },
      ],
    };
  if (example === 'unreadable')
    return {
      source: 'File exists, but opening is denied.',
      steps: [
        {
          title: 'Open the file',
          detail: 'Stop startup and explain the read error. Do not treat this as an empty file.',
          outcome: 'stop' as const,
        },
      ],
    };
  const steps: LoadStep[] = [
    {
      title: 'Open the file',
      detail: 'The file exists and its bytes can be read.',
      outcome: 'pass',
    },
  ];
  if (example === 'utf8') {
    steps.push({
      title: 'Decode UTF-8',
      detail: 'Byte FF cannot occur here in valid UTF-8. Stop; keep the original file.',
      outcome: 'stop',
    });
    return { source: 'Bytes (hexadecimal): 7B FF 7D', steps };
  }
  steps.push({
    title: 'Decode UTF-8',
    detail: 'The bytes become text. Text alone is not yet application state.',
    outcome: 'pass',
  });
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    steps.push({
      title: 'Parse JSON',
      detail: 'The text is unfinished JSON. Stop; keep the original file.',
      outcome: 'stop',
    });
    return { source: text, steps };
  }
  steps.push({
    title: 'Parse JSON',
    detail: 'JSON syntax is valid. Application rules have not been checked yet.',
    outcome: 'pass',
  });
  steps.push(...inspectStoredState(parsed));
  return { source: text, steps };
}

export function interruptedWriteFrame(step: number) {
  const previous = '{"next_id":2,"tasks":[{"id":1,"title":"Read notes","completed":false}]}';
  const proposed = '{"next_id":2,"tasks":[{"id":1,"title":"Read notes","completed":true}]}';
  const partial = proposed.slice(0, 43);
  return {
    previous,
    proposed,
    direct: step === 0 ? previous : step === 1 ? '' : step === 2 ? partial : proposed,
    safe: step < 4 ? previous : proposed,
    temporary: step === 0 || step >= 4 ? null : step === 1 ? '' : step === 2 ? partial : proposed,
  };
}
export type CopyMethod = 'direct' | 'shallow' | 'deep';
export type ChangeOperation = 'create' | 'complete' | 'delete' | 'blank' | 'missing';
export function commitFrame(
  step: number,
  method: CopyMethod,
  operation: ChangeOperation,
  failSave: boolean,
) {
  let accepted = { next_id: 2, tasks: [{ id: 1, title: 'Read notes', completed: false }] };
  const previous = copyStoredState(accepted);
  let candidate: StoredState | null = null;
  let disk = copyStoredState(previous);
  const rejected = operation === 'blank' || operation === 'missing';
  if (step >= 1 && !rejected) {
    candidate =
      method === 'direct'
        ? accepted
        : method === 'shallow'
          ? { ...accepted }
          : copyStoredState(accepted);
    if (operation === 'create') {
      candidate.tasks.push({ id: candidate.next_id, title: 'Café review', completed: false });
      candidate.next_id++;
    } else if (operation === 'complete') {
      const task = candidate.tasks[0];
      if (task) task.completed = true;
    } else candidate.tasks.splice(0, 1);
    if (step >= 2 && !failSave) disk = copyStoredState(candidate);
    if (step >= 3 && !failSave) accepted = copyStoredState(candidate);
  }
  return { accepted, candidate, disk, rejected, previous };
}
